// Empty so requests are relative to the page origin — the Go backend serves
// the embedded frontend in prod, and the rspack dev server proxies /api,
// /auth, /images, /swagger to the backend in dev.
export const baseUrl = "";

export class HttpError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
  }
}

type Options = Omit<RequestInit, "body"> & {
  body?: unknown;
  query?: Record<string, string | number | undefined>;
};

function buildUrl(path: string, query?: Options["query"]) {
  if (!query) return path;
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function http<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const { body, query, headers, ...rest } = opts;
  const isForm = body instanceof FormData;
  const res = await fetch(`${baseUrl}${buildUrl(path, query)}`, {
    credentials: "include",
    ...rest,
    headers: {
      ...(isForm ? {} : body !== undefined ? { "Content-Type": "application/json" } : {}),
      Accept: "application/json",
      ...headers,
    },
    body: isForm ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json") ? await res.json() : await res.text();

  if (!res.ok) {
    const msg = typeof payload === "object" && payload && "error" in payload
      ? String((payload as Record<string, unknown>).error)
      : res.statusText;
    throw new HttpError(res.status, payload, msg);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: Options["query"]) => http<T>(path, { method: "GET", query }),
  post: <T>(path: string, body?: unknown) => http<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => http<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => http<T>(path, { method: "PATCH", body }),
  del: <T>(path: string) => http<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return http<T>(path, { method: "POST", body: fd });
  },
};
